import { accountantKeys } from '../accountant/accountantKeys';
import { hrKeys } from '../hr/hrKeys';
import { managerKeys } from '../manager/managerKeys';
import { receptionKeys } from '../reception/receptionKeys';
import { salesKeys } from '../sales/salesKeys';

export interface QueryInvalidationTarget {
  queryKey: readonly unknown[];
}

function dedupeTargets(targets: QueryInvalidationTarget[]): QueryInvalidationTarget[] {
  const seen = new Set<string>();
  const unique: QueryInvalidationTarget[] = [];
  for (const target of targets) {
    const key = JSON.stringify(target.queryKey);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(target);
  }
  return unique;
}

export function getEmployeeInvalidations(): QueryInvalidationTarget[] {
  return [
    { queryKey: hrKeys.employees() },
    { queryKey: ['hr', 'employment-history'] },
    { queryKey: ['hr', 'controls', 'employees'] },
    { queryKey: managerKeys.employees() },
    { queryKey: managerKeys.bundle.employees() },
    { queryKey: salesKeys.bundle.managerAssignmentsScope() },
    { queryKey: salesKeys.bundle.managerScope() },
    { queryKey: salesKeys.pipeline.employees() },
    { queryKey: salesKeys.targets.all() },
  ];
}

const employeeEvents = new Set(['employee_created', 'employee_updated', 'employee_deleted', 'sales_employee_updated', 'sales_employee_vacation_updated']);

function scopedReceptionFallback(eventType: string): QueryInvalidationTarget[] {
  if (!eventType) return [];
  if (eventType.startsWith('student_')) {
    return [
      { queryKey: receptionKeys.students.all() },
      { queryKey: receptionKeys.waiting.all() },
      { queryKey: receptionKeys.bundle.studentsScope() },
      { queryKey: receptionKeys.bundle.studentInvoiceAll() },
      { queryKey: receptionKeys.materials.groupOptionsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType.startsWith('group_')) {
    return [
      { queryKey: receptionKeys.groups.all() },
      { queryKey: receptionKeys.bundle.groupsScope() },
      { queryKey: receptionKeys.materials.groupOptionsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType.startsWith('quarter_alert_settings_')) {
    return [
      { queryKey: receptionKeys.groups.all() },
      { queryKey: receptionKeys.bundle.groupsScope() },
      { queryKey: receptionKeys.quarter.settings() },
    ];
  }
  if (eventType.startsWith('course_interest_')) {
    return [
      { queryKey: receptionKeys.courseInterests.all() },
      { queryKey: receptionKeys.bundle.courseInterestsScope() },
      { queryKey: receptionKeys.students.all() },
      { queryKey: receptionKeys.bundle.studentsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType === 'new_refund_request') {
    return [
      { queryKey: receptionKeys.finance.all() },
      { queryKey: receptionKeys.bundle.financeScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType.startsWith('request_')) {
    return [
      { queryKey: receptionKeys.requests.all() },
      { queryKey: receptionKeys.bundle.requestsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType.startsWith('task_') || eventType.startsWith('other_task_') || eventType.startsWith('self_task_')) {
    return [{ queryKey: receptionKeys.bundle.tasksScope() }];
  }
  if (
    eventType === 'new_daily_report'
    || eventType === 'report_read'
    || eventType.startsWith('daily_report_')
    || eventType.startsWith('employee_daily_report_')
  ) {
    return [
      { queryKey: receptionKeys.dailyReports.all() },
      { queryKey: receptionKeys.dailyReports.unreadCount() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (
    eventType.startsWith('invoice_')
    || eventType.startsWith('installment_')
    || eventType.startsWith('installments_')
    || eventType.startsWith('sales_invoice_')
    || eventType.startsWith('finance_income_')
    || eventType.startsWith('finance_expense_')
    || eventType.startsWith('refund_')
  ) {
    return [
      { queryKey: receptionKeys.finance.all() },
      { queryKey: receptionKeys.bundle.financeScope() },
      { queryKey: receptionKeys.invoices.all() },
      { queryKey: receptionKeys.bundle.studentInvoiceAll() },
      { queryKey: receptionKeys.attendance.all() },
      { queryKey: receptionKeys.bundle.attendanceSessionAll() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType.startsWith('placement_payment_')) {
    return [
      { queryKey: receptionKeys.placement.all() },
      { queryKey: receptionKeys.bundle.placementPaymentsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType.startsWith('attempt_')) {
    return [
      { queryKey: receptionKeys.languageAdmin.attempts() },
      { queryKey: receptionKeys.attempts.all() },
    ];
  }
  if (eventType.startsWith('exam_')) {
    return [
      { queryKey: receptionKeys.exams.all() },
      { queryKey: receptionKeys.languageAdmin.placementExams() },
    ];
  }
  if (eventType.startsWith('sales_consult_')) {
    return [
      { queryKey: receptionKeys.salesConsult.all() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ];
  }
  if (eventType.startsWith('employee_') || eventType.startsWith('sales_lead_')) {
    return [{ queryKey: receptionKeys.bundle.homeScope() }];
  }
  if (eventType.startsWith('reception_log_') || eventType.startsWith('announcement_')) {
    return [
      { queryKey: receptionKeys.super.pendingBadges() },
      { queryKey: receptionKeys.bundle.superLogsScope() },
      { queryKey: receptionKeys.announcements.list() },
    ];
  }
  if (eventType.startsWith('material_')) {
    return [
      { queryKey: receptionKeys.materials.all() },
      { queryKey: receptionKeys.bundle.languageAdminMaterialsScope() },
      { queryKey: receptionKeys.languageAdmin.materials() },
    ];
  }
  if (eventType.startsWith('session_')) {
    return [
      { queryKey: receptionKeys.calendar.all() },
      { queryKey: receptionKeys.attendance.all() },
      { queryKey: receptionKeys.bundle.attendanceSessionAll() },
      { queryKey: receptionKeys.groups.all() },
      { queryKey: receptionKeys.bundle.groupsScope() },
    ];
  }
  if (eventType.startsWith('comment_')) {
    return [
      { queryKey: receptionKeys.students.all() },
      { queryKey: receptionKeys.bundle.studentInvoiceAll() },
    ];
  }
  // Conservative fallback without invalidating entire reception app.
  return [{ queryKey: receptionKeys.bundle.homeScope() }];
}

export function getReceptionInvalidationsForEvent(eventType: string): QueryInvalidationTarget[] {
  if (employeeEvents.has(eventType)) return [{ queryKey: receptionKeys.bundle.homeScope() }, ...getEmployeeInvalidations()];
  const exact: Record<string, QueryInvalidationTarget[]> = {
    reception_log_new: [
      { queryKey: receptionKeys.super.pendingBadges() },
      { queryKey: receptionKeys.bundle.superLogsScope() },
    ],
    reception_log_update: [
      { queryKey: receptionKeys.super.pendingBadges() },
      { queryKey: receptionKeys.bundle.superLogsScope() },
    ],
    announcement_created: [
      { queryKey: receptionKeys.super.pendingBadges() },
      { queryKey: receptionKeys.announcements.list() },
    ],
    announcement_deleted: [
      { queryKey: receptionKeys.super.pendingBadges() },
      { queryKey: receptionKeys.announcements.list() },
    ],
    announcement_approved: [{ queryKey: receptionKeys.super.pendingBadges() }],
    announcement_rejected: [{ queryKey: receptionKeys.super.pendingBadges() }],
    request_approved: [
      { queryKey: receptionKeys.requests.all() },
      { queryKey: receptionKeys.bundle.requestsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ],
    request_rejected: [
      { queryKey: receptionKeys.requests.all() },
      { queryKey: receptionKeys.bundle.requestsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ],
    new_daily_report: [
      { queryKey: receptionKeys.dailyReports.all() },
      { queryKey: receptionKeys.dailyReports.unreadCount() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ],
    report_read: [
      { queryKey: receptionKeys.dailyReports.all() },
      { queryKey: receptionKeys.dailyReports.unreadCount() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ],
    group_live_start: [
      { queryKey: receptionKeys.live.all() },
      { queryKey: receptionKeys.bundle.groupsScope() },
    ],
    group_live_stop: [
      { queryKey: receptionKeys.live.all() },
      { queryKey: receptionKeys.bundle.groupsScope() },
    ],
    sales_invoice_pending_approval: [
      { queryKey: receptionKeys.finance.all() },
      { queryKey: receptionKeys.bundle.financeScope() },
      { queryKey: receptionKeys.bundle.salesInvoicesScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ],
    sales_invoice_approved: [
      { queryKey: receptionKeys.finance.all() },
      { queryKey: receptionKeys.bundle.financeScope() },
      { queryKey: receptionKeys.bundle.salesInvoicesScope() },
      { queryKey: receptionKeys.invoices.all() },
      { queryKey: receptionKeys.bundle.studentInvoiceAll() },
      { queryKey: receptionKeys.courseInterests.all() },
      { queryKey: receptionKeys.bundle.courseInterestsScope() },
      { queryKey: receptionKeys.students.all() },
      { queryKey: receptionKeys.bundle.studentsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ],
    sales_invoice_converted: [
      { queryKey: receptionKeys.finance.all() },
      { queryKey: receptionKeys.bundle.financeScope() },
      { queryKey: receptionKeys.bundle.salesInvoicesScope() },
      { queryKey: receptionKeys.invoices.all() },
      { queryKey: receptionKeys.bundle.studentInvoiceAll() },
      { queryKey: receptionKeys.courseInterests.all() },
      { queryKey: receptionKeys.bundle.courseInterestsScope() },
      { queryKey: receptionKeys.students.all() },
      { queryKey: receptionKeys.bundle.studentsScope() },
      { queryKey: receptionKeys.bundle.homeScope() },
    ],
  };

  if (exact[eventType]) {
    return dedupeTargets(exact[eventType]);
  }
  return dedupeTargets(scopedReceptionFallback(eventType));
}

export function getAccountantInvalidationsForEvent(eventType: string): QueryInvalidationTarget[] {
  if (!eventType) return [];
  if (
    eventType.startsWith('invoice_')
    || eventType.startsWith('installment_')
    || eventType.startsWith('installments_')
    || eventType.startsWith('sales_invoice_')
    || eventType.startsWith('finance_income_')
    || eventType.startsWith('finance_expense_')
    || eventType.startsWith('placement_payment_')
    || eventType.startsWith('refund_')
    || eventType.startsWith('payroll_')
    || eventType.startsWith('accounting_month_')
    || eventType.startsWith('group_')
    || eventType.startsWith('student_added_to_group')
    || eventType.startsWith('student_removed_from_group')
    || eventType.startsWith('student_moved_group')
    || eventType.startsWith('employee_')
  ) {
    return [{ queryKey: accountantKeys.all }];
  }
  return [];
}

export function getHrInvalidationsForEvent(eventType: string): QueryInvalidationTarget[] {
  if (employeeEvents.has(eventType)) return getEmployeeInvalidations();
  if (eventType === 'message_received' || eventType === 'new_message' || eventType === 'message_read') {
    return [
      { queryKey: hrKeys.messages.list() },
      { queryKey: hrKeys.messages.stats() },
      { queryKey: hrKeys.dashboard() },
    ];
  }

  if (eventType === 'new_request' || eventType === 'request_updated') {
    return [
      { queryKey: hrKeys.requests.list() },
      { queryKey: hrKeys.requests.stats() },
      { queryKey: hrKeys.dashboard() },
    ];
  }

  if (eventType === 'employee_created' || eventType === 'employee_updated' || eventType === 'employee_deleted') {
    return [{ queryKey: hrKeys.employees() }];
  }

  if (
    eventType === 'payroll_updated'
    || eventType === 'payroll_item_added'
    || eventType === 'payroll_item_deleted'
    || eventType === 'payroll_finalized'
  ) {
    return [{ queryKey: hrKeys.payroll.all() }];
  }

  return [];
}

export function normalizeSalesEventType(eventType: string): string {
  if (!eventType) return '';
  if (eventType === 'priority_change') return 'priority_update';
  if (eventType === 'sales.lead.updated') return 'lead_updated';
  return eventType;
}

const salesPipelineEvents = new Set([
  'lead_created',
  'lead_updated',
  'lead_deleted',
  'lead_assigned',
  'lead_withdrawn',
  'lead_assigned_info',
  'status_change',
  'workflow_attempt',
  'tags_update',
  'priority_update',
  'conversion',
  'sales_invoice_converted',
  'sales_invoice_updated',
  'student_invoice_updated',
  'registration_via_link',
  'invoice_rejected',
  'cycle_consultations_updated',
  'cycle_proposal_updated',
  'negotiation_note',
]);

const salesTaskEvents = new Set([
  'lead_created',
  'lead_updated',
  'lead_deleted',
  'lead_assigned',
  'lead_withdrawn',
  'lead_assigned_info',
  'status_change',
  'workflow_attempt',
  'reminder',
  'reminder_completed',
  'interaction',
  'lead_history_updated',
  'priority_update',
  'registration_link_generated',
  'registration_link_opened',
  'registration_link_used',
  'registration_via_link',
  'cycle_consultations_updated',
  'cycle_proposal_updated',
  'negotiation_note',
]);

const salesHistoryEvents = new Set([
  'workflow_attempt',
  'interaction',
  'lead_history_updated',
  'negotiation_note',
  'conversion',
  'registration_link_generated',
  'registration_link_opened',
  'registration_link_used',
  'registration_via_link',
  'sales_invoice_converted',
  'sales_invoice_updated',
  'student_invoice_updated',
  'cycle_consultations_updated',
  'cycle_proposal_updated',
]);

const salesHomeEvents = new Set([
  'lead_created',
  'lead_updated',
  'lead_deleted',
  'lead_assigned',
  'lead_withdrawn',
  'lead_assigned_info',
  'status_change',
  'conversion',
  'sales_invoice_converted',
  'sales_invoice_updated',
  'student_invoice_updated',
  'registration_via_link',
  'invoice_rejected',
]);

const salesManagerEvents = new Set([
  'lead_assigned',
  'lead_withdrawn',
  'lead_assigned_info',
  'conversion',
  'sales_invoice_converted',
  'sales_invoice_updated',
  'student_invoice_updated',
  'invoice_rejected',
]);

function scopedSalesFallback(eventType: string): QueryInvalidationTarget[] {
  const normalizedType = normalizeSalesEventType(eventType);
  if (!normalizedType) return [];

  if (normalizedType === 'dashboard_delta') {
    return [];
  }

  const salesTargets: QueryInvalidationTarget[] = [];
  if (normalizedType === 'lead_withdrawn' || normalizedType === 'lead_assigned') {
    salesTargets.push({queryKey: ['sales', 'lead-warehouse']}, {queryKey: ['sales', 'warehouse-history']});
  }
  if (salesPipelineEvents.has(normalizedType)) {
    salesTargets.push(
      { queryKey: salesKeys.pipeline.all() },
      { queryKey: salesKeys.bundle.pipelineScope() },
    );
  }
  if (salesTaskEvents.has(normalizedType)) {
    salesTargets.push(
      { queryKey: salesKeys.tasks.all() },
      { queryKey: salesKeys.bundle.tasksScope() },
    );
  }
  if (salesHistoryEvents.has(normalizedType)) {
    salesTargets.push({ queryKey: salesKeys.history.all() });
  }
  if (salesHomeEvents.has(normalizedType)) {
    salesTargets.push({ queryKey: salesKeys.bundle.homeScope() });
  }
  if (['sales_invoice_converted', 'sales_invoice_updated', 'student_invoice_updated', 'invoice_rejected'].includes(normalizedType)) {
    salesTargets.push({ queryKey: [...salesKeys.targets.all(), 'overview'] });
  }
  if (
    salesManagerEvents.has(normalizedType)
    || salesPipelineEvents.has(normalizedType)
    || salesTaskEvents.has(normalizedType)
  ) {
    salesTargets.push({ queryKey: salesKeys.bundle.managerScope() });
  }
  if (salesTargets.length) {
    return salesTargets;
  }

  if (
    normalizedType === 'target_created'
    || normalizedType === 'target_updated'
    || normalizedType === 'target_deleted'
    || normalizedType === 'company_target_updated'
    || normalizedType === 'commission_tiers_updated'
  ) {
    return [
      { queryKey: [...salesKeys.targets.all(), 'overview'] },
      { queryKey: salesKeys.targets.progress() },
      { queryKey: salesKeys.targets.list() },
      { queryKey: salesKeys.targets.calculatedBonuses() },
      { queryKey: salesKeys.reports.all() },
      { queryKey: salesKeys.bundle.managerScope() },
    ];
  }

  if (normalizedType === 'competition_created' || normalizedType === 'competition_updated') {
    return [
      { queryKey: salesKeys.targets.competitions() },
      { queryKey: salesKeys.targets.competitionCurrent() },
      { queryKey: salesKeys.bundle.managerScope() },
    ];
  }

  if (normalizedType === 'bonus_status_updated' || normalizedType === 'bonuses_created') {
    return [
      { queryKey: salesKeys.targets.bonuses() },
      { queryKey: salesKeys.targets.calculatedBonuses() },
    ];
  }

  if (normalizedType === 'policy_bonuses_materialized') {
    return [
      { queryKey: salesKeys.targets.bonuses() },
      { queryKey: salesKeys.targets.policyOverview() },
      { queryKey: salesKeys.targets.calculatedBonuses() },
    ];
  }

  if (normalizedType === 'penalty_created' || normalizedType === 'penalty_status_updated') {
    return [
      { queryKey: salesKeys.targets.penalties() },
      { queryKey: salesKeys.targets.policyOverview() },
    ];
  }

  if (
    normalizedType === 'course_assignments_updated'
    || normalizedType === 'sales_employee_vacation_updated'
    || normalizedType === 'sales_employee_updated'
  ) {
    return [
      { queryKey: salesKeys.bundle.managerAssignmentsScope() },
      { queryKey: salesKeys.bundle.managerScope() },
      { queryKey: salesKeys.pipeline.all() },
    ];
  }

  if (normalizedType.startsWith('facebook_sheet_')) {
    return [{ queryKey: managerKeys.facebookMappings() }];
  }

  if (normalizedType === 'settings_stage_tags_updated') {
    return [
      { queryKey: salesKeys.pipeline.filters() },
      { queryKey: salesKeys.bundle.pipelineScope() },
      { queryKey: salesKeys.bundle.tasksScope() },
      { queryKey: salesKeys.tasks.all() },
    ];
  }

  return [{ queryKey: salesKeys.bundle.homeScope() }];
}

export function getSalesInvalidationsForEvent(eventType: string): QueryInvalidationTarget[] {
  if (employeeEvents.has(eventType) || eventType === 'course_assignments_updated') {
    return dedupeTargets([...getEmployeeInvalidations(), ...scopedSalesFallback(eventType)]);
  }
  return dedupeTargets(scopedSalesFallback(eventType));
}
